import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { WishlistOutlined } from 'jl-design-system/components/icons/WishlistOutlined';
import { AddToWishListNotification } from './AddToWishListNotification';
import { isMin1280pxWide } from '../../utils/device/identifyDevice';
import styles from './wishlist.scss';
import { NotificationDot } from '../notification-dot/NotificationDot';
import { EnvVarsFeaturesContext } from '../../scaffold/EnvVarsFeaturesContext';
import { UserContext } from '../../scaffold/UserContext';
import { isVariant } from '../../utils/monetate/getVariantData';
const popupDisplayDurationMs = 10000;

interface WishListProps {
  className?: string;
}

export const WishList: React.FC<WishListProps> = () => {
  const {
    envVars: { accountBaseUrl = '' },
    showWishListIconIsActive = '',
  } = useContext(EnvVarsFeaturesContext) as EnvVarsFeaturesContextValue;
  const {
    userState: {
      user: { signedIn = false, notifications },
    },
  } = useContext(UserContext) as { userState: UserProfile };

  const wishListUrl = !signedIn
    ? '/wish-list/guest'
    : `${accountBaseUrl}/wish-list`;
  const signInUrl = '/sign-in';
  const registerUrl = '/register';
  const [isNotificationDisplayed, setIsNotificationDisplayed] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startCloseTimer = useCallback(() => {
    // Clear any existing timer
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Start new timer
    timeoutRef.current = setTimeout(() => {
      setIsNotificationDisplayed(false);
    }, popupDisplayDurationMs);
  }, []);

  const handleWishListEvent = useCallback(() => {
    if (isMin1280pxWide() || isVariant('A', showWishListIconIsActive)) {
      setIsNotificationDisplayed(true);
      startCloseTimer();
    }
  }, [startCloseTimer]);

  const handleClosePopup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsNotificationDisplayed(false);
  };

  useEffect(
    function listenForGuestWishlistSuccess() {
      window.addEventListener(
        'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
        handleWishListEvent
      );

      return function stopListeningForGuestWishlistSuccess() {
        window.removeEventListener(
          'ADD_TO_WISHLIST_ESI:GUEST_SUCCESS',
          handleWishListEvent
        );
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    },
    [handleWishListEvent]
  );

  const hasNotification = notifications?.hasNotification ?? false;

  const label = `View your wishlist${hasNotification ? ', new notification' : ''}`;

  return (
    <>
      <a
        href={wishListUrl}
        className={styles.wrapper}
        data-test="wishlist-button"
        data-testid="wishlist-button"
        aria-label={label}
      >
        {hasNotification && <NotificationDot lgOnly />}
        <WishlistOutlined size="large" title={label} />
      </a>
      {!signedIn && isNotificationDisplayed && (
        <AddToWishListNotification
          signInUrl={signInUrl}
          registerUrl={registerUrl}
          onClose={handleClosePopup}
        />
      )}
    </>
  );
};
